import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { X, Paperclip, FileText, MessageCircle, Mail, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";

const waNumber = (num) => (num || "").replace(/[^0-9]/g, "");

/**
 * Shared "Send Document" picker — used by Accountant, Gov Helpers and Agents.
 * contact: { name, phone, whatsapp, email }
 * Per document: send the ACTUAL FILE (native share on phone / download on desktop),
 * or a secure open-link via WhatsApp / Email, or copy the link.
 */
const SendDocumentModal = ({ contact, onClose }) => {
  const { API, authHeader, token } = useAuth();
  const [docs, setDocs] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docFilter, setDocFilter] = useState("all");
  const [shipFilter, setShipFilter] = useState("all");
  const [sharingId, setSharingId] = useState(null);
  const [sharingAll, setSharingAll] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/trade/documents`, { headers: authHeader() }),
      axios.get(`${API}/trade/shipments`, { headers: authHeader() })
    ])
      .then(([d, s]) => {
        setDocs(d.data);
        setShipments(s.data);
      })
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  const docShareUrl = (d) => `${API}/trade/documents/shared/${d.share_id}`;
  const filteredDocs = docs.filter(d => {
    if (docFilter !== "all" && (d.category || "shipping") !== docFilter) return false;
    if (!["company", "roof", "misc", "usa"].includes(docFilter) && shipFilter !== "all") {
      if (shipFilter === "_unassigned") return (d.category || "shipping") === "shipping" && !d.shipment_id;
      return d.shipment_id === shipFilter;
    }
    return true;
  });

  const selectedShipmentName = shipments.find(s => s.id === shipFilter)?.shipment_name || "";
  const allFilesMessage = () => {
    const lines = filteredDocs.map((d, i) => `${i + 1}. ${d.original_name}\n${docShareUrl(d)}`).join("\n\n");
    return `${selectedShipmentName} — Goldspot Trade documents\n\n${lines}`;
  };

  const fetchFile = async (d) => {
    const response = await axios.get(
      `${API}/trade/documents/${d.id}/download?auth=${token}`,
      { responseType: "blob" }
    );
    return new File([response.data], d.original_name, {
      type: d.content_type || "application/octet-stream"
    });
  };

  const downloadFile = (blob, name) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", name);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Send the ACTUAL file (native share sheet on phone, download fallback on desktop)
  const shareActualFile = async (d) => {
    setSharingId(d.id);
    try {
      const file = await fetchFile(d);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: d.original_name });
          return;
        } catch (shareErr) {
          if (shareErr && shareErr.name === "AbortError") return; // user canceled
          // any other share failure falls through to download
        }
      }
      // Desktop / unsupported: download the file for manual attach
      const response = await axios.get(
        `${API}/trade/documents/${d.id}/download?auth=${token}`,
        { responseType: "blob" }
      );
      downloadFile(response.data, d.original_name);
      toast.success("File downloaded — attach it manually in WhatsApp, Telegram or Email");
    } catch {
      toast.error("Could not prepare the file");
    } finally {
      setSharingId(null);
    }
  };

  // Send ALL filtered files as actual files in one share (device-dependent)
  const shareAllActualFiles = async () => {
    setSharingAll(true);
    try {
      const files = [];
      for (const d of filteredDocs) {
        files.push(await fetchFile(d));
      }
      if (navigator.canShare && navigator.canShare({ files })) {
        await navigator.share({ files, title: `${selectedShipmentName} — documents` });
      } else {
        toast.info("This device sends one file at a time — use the File button on each document");
      }
    } catch {
      toast.error("Could not prepare the files");
    } finally {
      setSharingAll(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="attach-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="trade-card rounded-sm p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center">
              <Paperclip className="w-5 h-5 trade-gold" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-serif font-semibold text-lg">Send Document</h3>
              <p className="text-xs text-white/50">to {contact.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/40 hover:text-white transition-colors"
            data-testid="attach-close-btn"
          >
            <X size={18} />
          </button>
        </div>
        <p className="text-[11px] text-white/40 mb-4">
          "File" sends the actual document on your phone — the others send a secure open-link (no login needed).
        </p>

        {/* Category selector */}
        <div className="flex gap-2 mb-4">
          {[
            { value: "all", label: "All" },
            { value: "shipping", label: "Shipping" },
            { value: "company", label: "Company File" },
            { value: "roof", label: "Roof Sheeting" },
            { value: "misc", label: "Misc" },
            { value: "usa", label: "USA Files" },
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setDocFilter(f.value)}
              className={`flex-1 px-3 py-2 text-xs rounded-sm transition-all ${
                docFilter === f.value
                  ? "bg-[#D4AF37] text-black font-semibold"
                  : "bg-white/5 text-white/60 hover:text-white"
              }`}
              data-testid={`attach-filter-${f.value}`}
            >
              {f.label} ({f.value === "all" ? docs.length : docs.filter(d => (d.category || "shipping") === f.value).length})
            </button>
          ))}
        </div>

        {/* Shipment selector */}
        {!["company", "roof", "misc", "usa"].includes(docFilter) && (
          <div className="mb-4">
            <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5">Filter by shipment</p>
            <Select value={shipFilter} onValueChange={setShipFilter}>
              <SelectTrigger className="trade-input h-10 rounded-sm text-sm" data-testid="attach-shipment-select">
                <SelectValue placeholder="All shipments" />
              </SelectTrigger>
              <SelectContent className="bg-[#142039] border-white/10 max-h-56">
                <SelectItem value="all">All shipments</SelectItem>
                {shipments.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.shipment_name}</SelectItem>
                ))}
                <SelectItem value="_unassigned">Unassigned (no shipment)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Send whole shipment in one message */}
        {docFilter !== "company" && shipFilter !== "all" && shipFilter !== "_unassigned" && filteredDocs.length > 0 && (
          <div className="trade-card rounded-sm p-4 mb-4 border-[#D4AF37]/30" data-testid="send-all-block">
            <p className="text-xs text-white/60 mb-3">
              Send all {filteredDocs.length} file{filteredDocs.length === 1 ? "" : "s"} of <span className="trade-gold font-semibold">{selectedShipmentName}</span> in one go:
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={shareAllActualFiles}
                disabled={sharingAll}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs rounded-sm bg-white/10 text-white font-semibold hover:bg-white/20 transition-colors"
                data-testid="send-all-files-btn"
              >
                <Share2 size={14} /> {sharingAll ? "Preparing..." : `Actual Files (${filteredDocs.length})`}
              </button>
              {(contact.whatsapp || contact.phone) && (
                <a
                  href={`https://wa.me/${waNumber(contact.whatsapp || contact.phone)}?text=${encodeURIComponent(allFilesMessage())}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs rounded-sm bg-[#10b981] text-black font-semibold hover:bg-[#0da06a] transition-colors"
                  data-testid="send-all-whatsapp-btn"
                >
                  <MessageCircle size={14} /> WhatsApp All ({filteredDocs.length})
                </a>
              )}
              {contact.email && (
                <a
                  href={`mailto:${contact.email}?subject=${encodeURIComponent(`${selectedShipmentName} — All Documents`)}&body=${encodeURIComponent(allFilesMessage())}`}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs rounded-sm trade-btn-primary"
                  data-testid="send-all-email-btn"
                >
                  <Mail size={14} /> Email All ({filteredDocs.length})
                </a>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center p-8 text-white/40 text-sm">Loading documents...</div>
        ) : docs.length === 0 ? (
          <div className="text-center p-8 border border-white/5 rounded-sm">
            <FileText className="w-10 h-10 trade-gold mx-auto mb-3 opacity-40" strokeWidth={1} />
            <p className="text-white/50 text-sm">No documents yet — upload them in the Documents tab first</p>
          </div>
        ) : filteredDocs.length === 0 ? (
          <div className="text-center p-8 border border-white/5 rounded-sm">
            <p className="text-white/50 text-sm">No documents in this category</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredDocs.map(d => (
              <div
                key={d.id}
                className="flex items-center justify-between gap-3 p-3 bg-white/5 rounded-sm"
                data-testid={`attach-doc-${d.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-4 h-4 trade-gold flex-shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{d.original_name}</p>
                    <p className="text-[11px] text-white/40 truncate">
                      {(d.category || "shipping") === "company" ? "Company File" : (d.category === "roof" ? "Roof Sheeting" : (d.category === "misc" ? "Misc" : (d.category === "usa" ? "USA Files" : "Shipping")))} · {d.doc_type.replace(/_/g, " ")}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
                  <button
                    onClick={() => shareActualFile(d)}
                    disabled={sharingId === d.id}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-white/10 text-white font-semibold hover:bg-white/20 transition-colors"
                    data-testid={`attach-file-${d.id}`}
                    title="Send the actual file (native share on phone, download on desktop)"
                  >
                    <Share2 size={12} /> {sharingId === d.id ? "..." : "File"}
                  </button>
                  {(contact.whatsapp || contact.phone) && (
                    <a
                      href={`https://wa.me/${waNumber(contact.whatsapp || contact.phone)}?text=${encodeURIComponent(`${d.original_name} — Goldspot Trade document\n${docShareUrl(d)}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#10b981]/10 trade-green hover:bg-[#10b981]/20 transition-colors"
                      data-testid={`attach-whatsapp-${d.id}`}
                    >
                      <MessageCircle size={12} /> WhatsApp
                    </a>
                  )}
                  {contact.email && (
                    <a
                      href={`mailto:${contact.email}?subject=${encodeURIComponent(`Goldspot Trade Document — ${d.original_name}`)}&body=${encodeURIComponent(`Please find the document here:\n${docShareUrl(d)}`)}`}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                      data-testid={`attach-email-${d.id}`}
                    >
                      <Mail size={12} /> Email
                    </a>
                  )}
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(docShareUrl(d));
                        toast.success("Link copied");
                      } catch {
                        toast.error("Copy failed");
                      }
                    }}
                    className="px-3 py-2 text-xs text-white/60 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
                    data-testid={`attach-copy-${d.id}`}
                    title="Copy secure link"
                  >
                    <Copy size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default SendDocumentModal;
